import { useEffect, useRef } from "react"

import { isRadixFocusGuardOpen } from "@/lib"

const TEXT_ENTRY =
	'input, textarea, select, [contenteditable]:not([contenteditable="false"])'

/**
 * While rows are selected, Esc clears the selection and nothing else. It runs in
 * the capture phase and stops the event, so a page's own Esc (leave the thread,
 * switch the inbox tab) only fires once the selection is empty. A focused text
 * field or an open menu or dialog still gets Esc first.
 */
export function useEscapeClearsSelection(active: boolean, onClear: () => void) {
	const onClearRef = useRef(onClear)
	onClearRef.current = onClear

	useEffect(() => {
		if (!active) return
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key !== "Escape" || event.defaultPrevented) return
			if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey)
				return
			if (isRadixFocusGuardOpen()) return
			const target = event.target
			if (target instanceof HTMLElement && target.closest(TEXT_ENTRY)) return
			event.preventDefault()
			event.stopImmediatePropagation()
			onClearRef.current()
		}
		window.addEventListener("keydown", onKeyDown, true)
		return () => window.removeEventListener("keydown", onKeyDown, true)
	}, [active])
}
